/**
 * Checkout intent — what the client posts to `POST /api/paypal-orders/create`
 * under Full A (server owns money). The client sends **intent only** (which
 * listings, qty, shipping selection, optional accepted offer, buyer currency,
 * ship-to address). The server resolves intent against the DB and authors the
 * `purchase_units` itself — clients never compute amounts.
 *
 * @see .tondocs/payments/MONEY_OWNERSHIP_ROADMAP.md — Full A: client sends intent, server builds
 * @see .tondocs/payments/CHECKOUT_INTENT_CONTRACT.md — mobile-facing contract
 * @see lib/payments/error-codes.ts — PAYMENT_BREAKDOWN_MISMATCH (intent unpriceable)
 *
 * Step 1 deliverable: types only. The server-side resolver/builder that
 * consumes these types is **not** in scope yet — it lands with the create-route
 * cutover. The shape is intentionally minimal: every field the server cannot
 * authoritatively derive from `auth.user` + the DB.
 */

/**
 * Discriminated price-source tag carried on every checkout line.
 *
 * Three concrete sources live in DB:
 *   - `listings.price`               → `{ kind: 'listing' }`
 *   - `offers.amount`                → `{ kind: 'offer',  offerId }`
 *   - `listings.auction_highest_bid` → `{ kind: 'auction' }`
 *
 * The tag is REQUIRED on the wire. Pre-Finding-#16 the resolver chose between
 * "offer if `offerId` present else listing.price" — fine for two sources, but
 * silently broke the third (auctions): `listings.price` is NULL by DB CHECK
 * constraint, the resolver returned 422 / `price_unavailable`. Forcing the
 * client to declare its kind makes the switch exhaustive (compile-time check
 * via `never` in the resolver) and surfaces a future fourth source at the
 * type level instead of as a silent 422.
 *
 * @see .tondocs/payments/INTENT_CONTEXT_KIND.md — full spec
 * @see .tondocs/payments/CUTOVER_STEP_3_REVIEW.md §#16 — the bug this fixes
 */
export type CheckoutItemContext =
  | { kind: "listing" }
  | { kind: "offer"; offerId: string }
  | { kind: "auction" };

/**
 * Exhaustive allowlist of every `CheckoutItemContext["kind"]`. Boundary parsers
 * (route shape checks) iterate this constant instead of hardcoding `kind === "x"
 * || kind === "y"` chains, which silently miss new variants. The `_Assert*`
 * pins below make the array's set-equality with the union compile-enforced:
 * adding a kind to the union without updating the array — or vice versa —
 * fails the build.
 *
 * See `.tondocs/TEST_STRATEGY.md` §3 (exhaustiveness probe doctrine) and §3.4
 * (one union, one canonical declaration).
 */
export const CHECKOUT_ITEM_CONTEXT_KINDS = [
  "listing",
  "offer",
  "auction",
] as const;

// Compile-time exhaustiveness pin: the array must exactly cover
// CheckoutItemContext['kind']. Adding a kind to the union without
// updating the array — or vice versa — fails the build. This is a
// distinct invariant from the schema↔interface `pinned()` wrappers
// below (those pin Zod schemas to interfaces; this pins the literal
// allowlist to the union).
// See .tondocs/TEST_STRATEGY.md §3.
type _AssertKindsCoverUnion =
  CheckoutItemContext["kind"] extends (typeof CHECKOUT_ITEM_CONTEXT_KINDS)[number]
    ? true
    : never;
type _AssertNoExtraKinds =
  (typeof CHECKOUT_ITEM_CONTEXT_KINDS)[number] extends CheckoutItemContext["kind"]
    ? true
    : never;
// Reference both so unused-locals doesn't strip them:
const _kindPinCoverUnion: _AssertKindsCoverUnion = true;
const _kindPinNoExtras: _AssertNoExtraKinds = true;
void _kindPinCoverUnion;
void _kindPinNoExtras;

/**
 * Shipping-mode discriminator carried at the CheckoutIntent root.
 *
 * - `flat_rate` — buyer pre-selected one of the listing's `shipping_config`
 *   methods at cart time; every item carries a `shippingMethodId`. Server
 *   reads price/extraValue/allowCombinedShipping from the row.
 * - `live_rate` — listing is in `listings.shipping_mode='live_rate'`; buyer
 *   picks a carrier-provider rate at checkout via the live-rate sub-flow.
 *   The selection lives at intent root (`externalShipping`) — one rate per
 *   checkout under the single-seller invariant. Items have no
 *   `shippingMethodId` on this branch.
 *
 * PR-E Move 2 (Sibling-F · F2, 2026-06-27): the discriminator moved from
 * per-item to root. Pre-Move-2 each `CheckoutIntentItem` carried `kind:
 * 'flat_rate' | 'live_rate'`. Under the single-seller invariant + per-seller
 * `listings.shipping_mode`, every item in one checkout shares the same kind —
 * the per-item `kind` was an N-way redundancy (8 items × the same literal is
 * noise) and the resolver had to partition by `item.kind` to know whether
 * `externalShipping` was even relevant. Lifting `kind` to root mirrors
 * `CheckoutPayload`'s structural shape, eliminates the partition, and makes
 * `externalShipping` REQUIRED on the live_rate branch (illegal state "live
 * rate without rate-lock" unrepresentable) — same MISU win as PR-E Move 4
 * applies on the CAPTURE side.
 *
 * @see .tondocs/shippo/PLAN.md §D WS4 (per-seller routing)
 * @see CheckoutIntent — discriminated union at root
 *
 * The resolver verifies `intent.kind` matches the listing's
 * `listings.shipping_mode` for every item; a mismatch is treated as
 * stale-cart (PAYMENT_BREAKDOWN_MISMATCH).
 */
export const CHECKOUT_ITEM_KINDS = ["flat_rate", "live_rate"] as const;

export interface CheckoutIntentItemBase {
  /** `listings.id`. Server reads price + seller + shipping_config from this row. */
  listingId: string;
  /** Positive integer. Server validates against `listings.quantity` (stock). */
  quantity: number;
  /**
   * REQUIRED. Discriminator that picks the DB column the resolver uses as
   * unit price. See `CheckoutItemContext` doc for rationale + branches.
   * Missing or unknown `kind` → 400 INVALID_INTENT_SHAPE at the route layer.
   */
  context: CheckoutItemContext;
}

/**
 * Buyer-pre-selected flat-rate line. `shippingMethodId` is required and must
 * appear in the listing's `shipping_config` (resolver step 4).
 *
 * PR-E Move 2 (Sibling-F · F2): dropped per-item `kind` discriminator — moved
 * to {@link CheckoutIntent} root.
 */
export interface CheckoutIntentFlatRateItem extends CheckoutIntentItemBase {
  /**
   * `shipping_methods.id` chosen for this line. Must be referenced by the
   * listing's `shipping_config` and resolvable to the buyer's ship-to country.
   * Server reads price/extraValue/allowCombinedShipping from the row.
   */
  shippingMethodId: string;
}

/**
 * Live-rate line. Carries no `shippingMethodId` — the buyer's locked carrier
 * rate lives in {@link CheckoutIntentLiveRate.externalShipping} at root.
 *
 * PR-E Move 2 (Sibling-F · F2): dropped per-item `kind` discriminator — moved
 * to {@link CheckoutIntent} root.
 */
export type CheckoutIntentLiveRateItem = CheckoutIntentItemBase;

/**
 * Back-compat alias — the union of both per-branch item shapes. Under PR-E
 * Move 2 the discriminator is at {@link CheckoutIntent} root; each variant's
 * `items: CheckoutIntent*Item[]` array is structurally narrowed once
 * `intent.kind` is checked. Kept here so existing consumers that reference
 * `CheckoutIntentItem` compile through the move.
 */
export type CheckoutIntentItem =
  | CheckoutIntentFlatRateItem
  | CheckoutIntentLiveRateItem;

/**
 * Ship-to address as captured by the buyer (or selected from `address_book`).
 * Server uses it to (1) validate `shipping_methods.banned_countries`,
 * (2) populate PayPal `shipping.address`, (3) persist on the resulting order.
 *
 * Field names mirror the existing `ShippingFormData` shape so the web client
 * change is mechanical. Country is the long-form name (e.g. "France") —
 * server normalises to ISO-3166 alpha-2 the same way the current client does
 * (`getCountryCodeByName`).
 *
 * Sibling-F · F2 (PR-E Move 1, 2026-06-27): this is the canonical buyer-
 * destination address shared across CREATE and CAPTURE channels. Pre-PR-E
 * the CAPTURE channel (`CheckoutPayload`) declared its own
 * `CheckoutPayloadShippingInfo` with slightly different optionality (state
 * required, apartment required-but-empty-allowed, plus a composed `phone`).
 * That parallel declaration was the seed of the next drift class — collapsed
 * here.
 *
 * Stricter-merge picks vs pre-PR-E shapes:
 *   - `state` lifted from optional → REQUIRED for ALL countries. Aligns with
 *     `isShippingAddressComplete` + recent ShippingForm fixes (`09bb3683`,
 *     `655ad02c`, `347355f3`). Pre-fix, state required only for US/CA; the
 *     desync silently blocked PayPal/Stripe rails for FR/UK/JP buyers.
 *   - `apartment`, `phoneCountryCode`, `phoneNumber` stay optional —
 *     legitimately absent for many addresses; required-but-empty (the prior
 *     Payload shape) is the same effective semantics with a worse type.
 *   - Composed `phone` field dropped — was redundant (recomposable from
 *     `phoneCountryCode + phoneNumber`); no consumer reads it without
 *     recomposing.
 */
export interface CheckoutIntentAddress {
  firstName: string;
  lastName: string;
  address: string;
  apartment?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  email: string;
  phoneCountryCode?: string;
  phoneNumber?: string;
}

/**
 * The complete request body for the intent-shaped create route.
 *
 * Field-naming choices (flagged in CHECKOUT_INTENT_CONTRACT.md so mobile and
 * the doc agree):
 *  - `items` (not `lineItems` / `purchase_units`)
 *  - `shippingMethodId` per item (matches roadmap line ~156, drops the
 *    `selected` prefix used in `API_CONTRACT_HARDENING.md` C4 — shorter,
 *    less redundant; the intent context already implies "selected").
 *  - `currency` is the buyer's chosen currency for the whole order (PayPal
 *    enforces a single currency per order, mirrors the current
 *    `currencyCode` prop on `paypal-checkout.tsx`).
 *  - `shippingAddress` is REQUIRED — server cannot author `purchase_units`
 *    without knowing the destination (banned-countries check + PayPal
 *    `shipping` block).
 */
/**
 * Common fields on every CheckoutIntent, regardless of shipping mode. The
 * discriminated variants {@link CheckoutIntentFlatRate} and
 * {@link CheckoutIntentLiveRate} extend this base.
 */
export interface CheckoutIntentBase {
  /** ISO-4217 (e.g. "USD", "EUR", "JPY"). Single currency per order. */
  currency: string;
  /** Buyer-chosen ship-to. Required — see field doc above. */
  shippingAddress: CheckoutIntentAddress;
  /**
   * `profiles.id` of the seller this checkout targets. Single-seller invariant
   * lives on the contract root: every checkout pays exactly one seller. The
   * server verifies that every `items[].listingId` resolves to a row with
   * `seller_id === sellerId`; mismatch → 400 `INTENT_SELLER_MISMATCH`.
   *
   * Why on the root and not per-item: client cannot accidentally split items
   * across sellers; resolver knows the answer without a `groupBy`; mobile /
   * embed surfaces pass one field instead of deriving.
   *
   * @see .tondocs/payments/SINGLE_SELLER_INVARIANT_SPEC.md
   */
  sellerId: string;
}

/**
 * Flat-rate intent — buyer pre-selected `shippingMethodId` per item at cart
 * time. No `externalShipping` field exists on this variant — `tsc` rejects
 * the access. Mirror of {@link CheckoutPayloadFlatRate}.
 */
export interface CheckoutIntentFlatRate extends CheckoutIntentBase {
  kind: "flat_rate";
  /** Non-empty. Server rejects empty arrays with INVALID_PURCHASE_UNITS. */
  items: CheckoutIntentFlatRateItem[];
}

/**
 * Live-rate intent — buyer locked a carrier rate at checkout via the
 * live-rate sub-flow. `externalShipping` is REQUIRED here (not optional);
 * illegal state "live_rate without rate-lock" is unrepresentable. Mirror
 * of {@link CheckoutPayloadLiveRate}.
 *
 * Single-seller invariant: there is exactly one rate to lock (the contract
 * is one-intent-one-seller). `sellerProfileId` on `externalShipping` is
 * implicit — always equals `intent.sellerId`.
 *
 * @see .tondocs/shippo/PLAN.md §K.6 (provider-neutral rename)
 * @see .tondocs/shippo/PLAN.md §L (ship-ready trigger)
 * @see .tondocs/payments/SINGLE_SELLER_INVARIANT_SPEC.md
 */
export interface CheckoutIntentLiveRate extends CheckoutIntentBase {
  kind: "live_rate";
  /** Non-empty. Server rejects empty arrays with INVALID_PURCHASE_UNITS. */
  items: CheckoutIntentLiveRateItem[];
  /**
   * Buyer-locked carrier-provider rate captured at checkout. Required on
   * this branch — the live-rate sub-flow gates emission of the intent on
   * this selection.
   *
   * Renamed 2026-06-17 from `shippoShipping` as part of the provider-neutral
   * rename (PLAN §K.6, S3). The wire shape is unchanged; the field key
   * dropped its provider-specific name. PR-E Move 2 (2026-06-27) further
   * lifted the discriminator from per-item to root so this field can be
   * REQUIRED (not optional) here.
   */
  externalShipping: CheckoutIntentExternalShippingSelection;
}

/**
 * The complete request body for the intent-shaped create route. Discriminated
 * union on `kind` (mirror of {@link CheckoutPayload}).
 *
 * Field-naming choices (flagged in CHECKOUT_INTENT_CONTRACT.md so mobile and
 * the doc agree):
 *  - `items` (not `lineItems` / `purchase_units`)
 *  - `shippingMethodId` per item on `flat_rate` — matches roadmap line ~156,
 *    drops the `selected` prefix used in `API_CONTRACT_HARDENING.md` C4.
 *  - `currency` is the buyer's chosen currency for the whole order (PayPal
 *    enforces a single currency per order).
 *  - `shippingAddress` is REQUIRED — server cannot author `purchase_units`
 *    without knowing the destination.
 *
 * PR-E Move 2 (Sibling-F · F2, 2026-06-27): discriminated on root `kind`
 * (was per-item). `externalShipping` on the live_rate branch is REQUIRED
 * (was optional at root) — illegal state unrepresentable.
 */
export type CheckoutIntent = CheckoutIntentFlatRate | CheckoutIntentLiveRate;

/**
 * Live-rate intent BEFORE the buyer has locked a carrier rate. Identical to
 * {@link CheckoutIntentLiveRate} except `externalShipping` is optional. The
 * wire contract ({@link CheckoutIntent} + {@link CheckoutIntentSchema}) keeps
 * `externalShipping` REQUIRED — illegal-state-unrepresentable on the wire
 * stays intact.
 *
 * This shape only exists between the client cart builder
 * ({@link buildCheckoutIntent}) and the client checkout component
 * ({@link components/cart/paypal-checkout.tsx}), which owns the live-rate
 * sub-flow FSM. The component narrows draft → {@link CheckoutIntent} by
 * folding in `externalShipping` once the buyer confirms a rate, before any
 * POST.
 *
 * Why this exists: pre-F2 (2026-06-26) `externalShipping` was optional at
 * intent root, which let `buildCheckoutIntent` produce a valid live_rate
 * intent at cart time (before the FSM had locked a rate). F2 lifted the
 * discriminator to root and made `externalShipping` required — correct for
 * the wire, but the cart-layer call site cannot supply the lock (FSM lives
 * one layer down in `PayPalCheckout`). Without a draft type the cart-layer
 * builder returned null for every live_rate seller → `isButtonDisabled`
 * stayed true → PayPal SDK never initialised, Stripe button disabled.
 * See [[feedback_left_fix_audit_downstream_branches]].
 */
export interface CheckoutIntentLiveRateDraft extends CheckoutIntentBase {
  kind: "live_rate";
  items: CheckoutIntentLiveRateItem[];
  externalShipping?: CheckoutIntentExternalShippingSelection;
}

/**
 * Client-side draft intent — the return type of {@link buildCheckoutIntent}.
 * Flat-rate is identical to the wire shape (no draft state); live-rate
 * allows `externalShipping` to be absent at construction time.
 *
 * Narrowed back to {@link CheckoutIntent} by `PayPalCheckout` before the
 * intent is posted (see `effectiveIntent` in that component).
 */
export type CheckoutIntentDraft =
  | CheckoutIntentFlatRate
  | CheckoutIntentLiveRateDraft;

// Compile-time exhaustiveness pin for `CHECKOUT_ITEM_KINDS` ↔ root union.
// Mirror of the {@link CheckoutPayload} `_AssertPayloadKindsCoverUnion`.
type _AssertIntentKindsCoverUnion =
  CheckoutIntent["kind"] extends (typeof CHECKOUT_ITEM_KINDS)[number]
    ? true
    : never;
type _AssertNoExtraIntentKinds =
  (typeof CHECKOUT_ITEM_KINDS)[number] extends CheckoutIntent["kind"]
    ? true
    : never;
const _intentKindPinCoverUnion: _AssertIntentKindsCoverUnion = true;
const _intentKindPinNoExtras: _AssertNoExtraIntentKinds = true;
void _intentKindPinCoverUnion;
void _intentKindPinNoExtras;

/**
 * Buyer-locked carrier-provider rate selection captured at checkout. The
 * server re-rates via the provider (today Shippo) using `rateId` to confirm
 * `amount` is still the quoted price before charging the buyer.
 *
 * Renamed 2026-06-17 from `CheckoutIntentShippoSelection` as part of the
 * provider-neutral rename (PLAN §K.6, S3). The wire shape is unchanged.
 */
export interface CheckoutIntentExternalShippingSelection {
  /** Carrier-provider Rate `object_id` chosen by the buyer (Shippo today). */
  rateId: string;
  /** Carrier-provider Shipment `object_id` the rate belongs to (Shippo today). */
  shipmentId: string;
  /** Rate amount shown to the buyer (USD), before the optional safety buffer. */
  amount: number;
}

// ---------------------------------------------------------------------------
// Server-side resolution outcome
// ---------------------------------------------------------------------------

/**
 * Discriminated outcome of "parse + validate intent against the DB". This is
 * the seam between the route handler and the builder: on `ok` the builder
 * has everything typed and DB-resolved; on `error` the route returns the
 * carried `code` (currently always `PAYMENT_BREAKDOWN_MISMATCH` for the
 * stale-/removed-/banned- cases).
 *
 * The concrete `resolved` shape is intentionally left as a thin marker
 * (`unknown` payload + a tagged kind) at Step 1 — the resolver/builder lands
 * in a later step and will own that shape. Locking the resolved-data type
 * now would risk churn; locking the *outcome* shape is what the route
 * handler needs to be coded against.
 */
export type IntentResolutionResult =
  | {
      kind: "ok";
      /**
       * DB-resolved, typed payload ready for the builder. Shape is fixed by
       * the resolver implementation; route handler treats it as opaque.
       */
      resolved: ResolvedCheckout;
    }
  | {
      kind: "error";
      /**
       * Resolver error codes:
       *  - `PAYMENT_BREAKDOWN_MISMATCH` — stale `listingId`, removed
       *    `shippingMethodId`, expired `offerId`, banned-country shipping,
       *    price/stock changes between cart and create. Surfaces as 422.
       *  - `INTENT_SELLER_MISMATCH` — at least one `items[].listingId`
       *    resolves to a row with `seller_id !== intent.sellerId`. Surfaces
       *    as 400 (client bug, not stale-cart). Single-seller invariant —
       *    see SINGLE_SELLER_INVARIANT_SPEC.md.
       *
       * Other failure modes (auth, env) stay on their existing codes
       * upstream of the resolver.
       */
      code: "PAYMENT_BREAKDOWN_MISMATCH" | "INTENT_SELLER_MISMATCH";
      /** Human-readable, safe to surface to the buyer (e.g. "Listing no longer available"). */
      message: string;
      /**
       * Optional structured detail for client UX (e.g. which listing went
       * stale, so the cart can flag it). Stable keys only; no PII.
       */
      details?: {
        listingId?: string;
        shippingMethodId?: string;
        offerId?: string;
        sellerId?: string;
        reason?: string;
      };
    };

// ---------------------------------------------------------------------------
// Resolved checkout — concrete shape (Step 2, owned by resolve-checkout-intent)
// ---------------------------------------------------------------------------

/**
 * `ResolvedCheckout` is the DB-resolved, server-trusted shape that the PayPal
 * mapper consumes. Every money field here is server-derived; the resolver
 * derives it from `listings`/`offers`/`shipping_methods` + `lib/config.ts`
 * fee constants. Mapper does no money math — it places these numbers into
 * the PayPal `purchase_units[]` shape verbatim.
 *
 * `ResolvedIntent` (Step 1 marker) is kept as an alias so callers that already
 * type against the seam keep compiling. New code targets `ResolvedCheckout`.
 *
 * @see lib/payments/resolve-checkout-intent.ts — producer
 * @see lib/payments/build-paypal-purchase-units.ts — consumer
 */
export interface ResolvedCheckoutLineItem {
  /** `listings.id`. */
  listingId: string;
  /** Mirrored from the listing — used for PayPal `items[].name`. */
  name: string;
  /** Positive integer. */
  quantity: number;
  /**
   * Server-authoritative unit price. From `offers.amount` when the line had
   * an `offerId`, otherwise `listings.price`. Never from the intent.
   */
  unitAmount: number;
  /** `unitAmount × quantity`, rounded to currency precision. */
  lineTotal: number;
  /**
   * Shipping method resolved against the listing's `shipping_config`. Carries
   * everything `calculateSellerShippingTotal` needs.
   */
  shippingMethod: {
    id: string;
    name: string;
    price: number;
    extraValue: number;
    allowCombinedShipping: boolean;
  };
  /** Present only when an offer overrode the listing price. */
  offerId?: string;
}

/**
 * Common fields on every resolved seller group, regardless of shipping mode.
 * The {@link ResolvedCheckoutSellerGroup} discriminated union extends this
 * shape per-branch — `legacy` declares no carrier-rate fields at all,
 * `live_rate` carries them required.
 */
export interface ResolvedCheckoutSellerGroupBase {
  /** `profiles.id` — also the PayPal `purchase_unit.reference_id`. */
  sellerProfileId: string;
  /** `profiles.username` for descriptors / emails — not load-bearing for money. */
  sellerHandle: string | null;
  /** PayPal sub-merchant id — `purchase_unit.payee.merchant_id`. */
  payeeMerchantId: string;
  items: ResolvedCheckoutLineItem[];
  /** Sum of `item.lineTotal`. */
  itemTotal: number;
  /** From `calculateSellerShippingTotal` over `items`. */
  shippingTotal: number;
  /** Buyer-paid handling = this seller's slice of the order service fee. */
  serviceFee: number;
  /**
   * PayPal `payment_instruction.platform_fees[0].amount`.
   *  - legacy: `SELLING_FEE_RATE × (itemTotal+shippingTotal) + serviceFee`
   *    (seller keeps shipping).
   *  - live_rate: the above PLUS `shippingTotal`, so the shipping the buyer
   *    paid is routed to Kraahl (which buys the label) instead of to the
   *    seller. The seller then nets `itemTotal` only.
   *    See `lib/payments/shippo-money.ts::foldShippoShippingIntoPlatformFee`.
   */
  platformFee: number;
  /** `itemTotal + shippingTotal + serviceFee`. Equals PayPal `amount.value`. */
  grandTotal: number;
}

/**
 * Legacy (flat-rate) seller group — seller-defined flat shipping price, seller
 * keeps shipping. No carrier-provider rate-lock fields are declared on this
 * branch, so `tsc` red-lines any consumer that tries to read
 * `g.externalRateId` / `g.externalShipmentId` / `g.carrierProvider` after
 * narrowing on `shippingMode === 'legacy'`.
 */
export interface ResolvedCheckoutSellerGroupLegacy
  extends ResolvedCheckoutSellerGroupBase {
  shippingMode: "legacy";
}

/**
 * Live-rate seller group — the buyer locked a carrier-provider rate at
 * checkout; Kraahl funds the label (bought at the seller's Ready-to-ship
 * click, PLAN §L), and the buyer-paid shipping is folded into `platformFee`
 * via `lib/payments/shippo-money.ts::foldShippoShippingIntoPlatformFee`.
 *
 * `externalRateId`, `externalShipmentId`, `carrierProvider` are REQUIRED on
 * this branch — the illegal state "live_rate group without a rate-lock" is
 * unrepresentable. Same MISU pattern as `CheckoutPayloadLiveRate`
 * (`./checkout-payload.ts`), but applied to the server-internal resolved
 * shape (no wire side).
 *
 * Renamed 2026-06-17 from the historical `'shippo'` literal as part of
 * the provider-neutral rename (PLAN §K.6, S3). Mirrors the renamed
 * `listings.shipping_mode` CHECK enum.
 *
 * @see .tondocs/shippo/PLAN.md §C
 * @see .tondocs/shippo/PLAN.md §K.6
 */
export interface ResolvedCheckoutSellerGroupLiveRate
  extends ResolvedCheckoutSellerGroupBase {
  shippingMode: "live_rate";
  /**
   * Locked carrier-provider rate id — the label-buy happens later at the
   * seller's Ready-to-ship click (PLAN §L), with a fresh re-rate against
   * this shipment.
   *
   * Renamed 2026-06-17 from `shippoRateId` (PLAN §K.6, S3).
   */
  externalRateId: string;
  /**
   * Locked carrier-provider shipment id — kept so the Ready-to-ship endpoint
   * can re-rate against the same shipment.
   *
   * Renamed 2026-06-17 from `shippoShipmentId` (PLAN §K.6, S3).
   */
  externalShipmentId: string;
  /**
   * Which provider issued `externalRateId` / `externalShipmentId`. Mirrors
   * the renamed `seller_orders.carrier_provider` column. Single value today
   * (`'shippo'`); reserved for sendcloud/easypost.
   *
   * @see .tondocs/shippo/PLAN.md §K.6
   */
  carrierProvider: CarrierProvider;
  /**
   * Rate fold detail — the same shipping amount seen two ways:
   *  - rawAmount: pre-buffer carrier cost; ends up in platform_fees (Kraahl
   *    buys the label with this).
   *  - bufferedAmount: buyer-charged (raw + BUFFER_ABS + BUFFER_PCT × raw);
   *    ends up in the wire's shipping component.
   * Snapshotted at CREATE and stored in checkout_wire_snapshots.raw for
   * audit / drift diagnosis (CAPTURED_FACTS_SPEC.md §2.2).
   */
  foldDetail: {
    rawAmount: number;
    bufferedAmount: number;
  };
}

/**
 * Server-internal resolved seller group. Discriminated on `shippingMode` —
 * one canonical declaration, mirrored across the producer (resolver) and the
 * single consumer (`foldShippoShippingIntoPlatformFee` in `shippo-money.ts`).
 *
 * Pre-MISU this was a flat interface with `shippingMode?: 'legacy' |
 * 'live_rate'` + three independent `externalRateId?` / `externalShipmentId?`
 * / `carrierProvider?` optionals — same "optional-fields-depending-on-kind"
 * anti-pattern that produced the {@link
 * .tondocs/shippo/HANDOFF_EXTERNAL_SHIPPING_CAPTURE_GAP_2026_06_26.md}
 * incident on the wire-shaped `CheckoutPayload` sibling. F15 lifts the
 * same axis here so future carriers (`sendcloud`, `easypost`) can't
 * silently drift the optional-field pattern.
 *
 * Not a wire shape (server-internal); no money-bleed risk today. Pure
 * hygiene + future-proofing.
 *
 * @see ./checkout-payload.ts — wire-shape sibling (`CheckoutPayload` MISU)
 * @see .tondocs/RELIABILITY_ROADMAP.md §3a Sibling-F · F15
 * @see .tondocs/skills/type-driven-design.md §1 (ADT), §2 (illegal-states), §3 (`never`)
 */
export type ResolvedCheckoutSellerGroup =
  | ResolvedCheckoutSellerGroupLegacy
  | ResolvedCheckoutSellerGroupLiveRate;

export interface ResolvedCheckoutShippingAddress {
  fullName: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  /** PayPal `admin_area_1` form — normalised to code for US/CA, free text otherwise. */
  state: string;
  postalCode: string;
  /** ISO-3166 alpha-2 (PayPal C2 for China). Normalised from the long-form intent. */
  countryCode: string;
  email: string;
  phoneCountryCode?: string;
  phoneNumber?: string;
}

export interface ResolvedCheckout {
  readonly __brand: "ResolvedCheckout";
  /** ISO-4217. Single value — multi-currency carts are a resolver error. */
  currency: string;
  /** True for JPY/TWD/HUF — mapper uses integer formatting. */
  zeroDecimal: boolean;
  shippingAddress: ResolvedCheckoutShippingAddress;
  /**
   * The single seller this checkout pays. Structural enforcement of the
   * single-seller invariant — the resolver cannot build a `ResolvedCheckout`
   * spanning more than one seller; downstream code cannot read "the other"
   * seller because the type has no array.
   *
   * Order-level subtotal / shipping / service-fee / grand-total are NOT
   * mirrored on the root — they equal `sellerGroup.{itemTotal, shippingTotal,
   * serviceFee, grandTotal}` by construction. Two ways to express the same
   * number is the kind of leak that produces drift bugs. Readers go through
   * `sellerGroup.*`.
   *
   * @see .tondocs/payments/SINGLE_SELLER_INVARIANT_SPEC.md
   */
  sellerGroup: ResolvedCheckoutSellerGroup;
}

/**
 * Step 1 marker, kept as an alias for backwards-naming. New code → `ResolvedCheckout`.
 * @deprecated use `ResolvedCheckout`
 */
export type ResolvedIntent = ResolvedCheckout;

// ---------------------------------------------------------------------------
// Transport — the request body shape of `POST /api/paypal-orders/create`
// ---------------------------------------------------------------------------

/**
 * Funding-source hint mirroring `lib/payments/paypal-create-validation.ts`.
 * Declared inline (rather than re-exporting) so this module stays the single
 * import for both clients and route handler — the validation module re-exports
 * a stricter `FundingSource` literal type the route consumes.
 */
export type CheckoutFundingSource =
  | "paypal"
  | "paylater"
  | "card"
  | "applepay"
  | "googlepay"
  | "venmo";

/**
 * Trusted slice of the client-supplied `payment_source` block. Today only
 * `google_pay` is accepted from the client (paypal/card/venmo are inferred
 * server-side from `fundingSource`). `apple_pay` is reserved — wired the same
 * way as google_pay when native Apple Pay lands; the route currently treats
 * it as "absent" via the existing `normalizePaymentSource` helper.
 */
export interface CheckoutPaymentSource {
  google_pay?: {
    attributes?: {
      verification?: { method?: string };
    };
  };
  apple_pay?: {
    attributes?: {
      verification?: { method?: string };
    };
  };
}

/**
 * Full request body for `POST /api/paypal-orders/create`. `intent` is the
 * money authority (CheckoutIntent); everything else is transport / per-request
 * concern that the server passes through unchanged.
 *
 * Why this lives next to `CheckoutIntent` rather than in
 * `paypal-create-validation.ts`: `CheckoutIntent` is the money contract that
 * mobile and web share, the transport wrapper is what they post. Keeping both
 * here makes a single import the canonical request shape.
 */
export interface CreateOrderRequest {
  /** Server resolves this against the DB and builds purchase_units. */
  intent: CheckoutIntent;
  /**
   * Rail hint. Drives the soft_descriptor (`card` → "KRAAHL <handle>", anything
   * else → bare "KRAAHL") and is echoed back as `?fundingSource=` on the
   * approvalUrl so PayPal renders the right rail.
   */
  fundingSource?: CheckoutFundingSource;
  /**
   * Trusted client slice for native digital-wallet flows. Single-seller
   * invariant means each PayPal order has exactly one purchase unit, so the
   * historical `DIGITAL_WALLET_MULTI_SELLER` rejection class is now
   * unreachable through the typed pipeline — it survives only as a raw-HTTP
   * defence-in-depth backstop.
   *
   * @see .tondocs/payments/SINGLE_SELLER_INVARIANT_SPEC.md
   */
  paymentSource?: CheckoutPaymentSource;
  /**
   * Native mobile deep-link return URLs. Passed through to PayPal's
   * `application_context.return_url` / `cancel_url`. Mutually exclusive with
   * `paymentSource` (PayPal forbids both on one order).
   */
  mobileReturnUrl?: string;
  mobileCancelUrl?: string;
  /**
   * Sandbox-only negative-testing code (PayPal-Mock-Response). Honoured only
   * in sandbox + when `PAYPAL_NEGATIVE_TESTING` is true; ignored otherwise.
   */
  mockCode?: string;
}

// ---------------------------------------------------------------------------
// Runtime parse — Zod schemas
//
// The interfaces above are the documented contract surface; the schemas below
// are the boundary parser. Each `export const *Schema` is wrapped in
// `pinned<Interface>()(...)` — the wrapper is the compile-time
// schema↔interface pin (see `./pin.ts`). Adding a field to one side without
// the other fails `tsc --noEmit` at the export site.
//
// Choice (WEB_MOBILE_CONTRACT §8 G): keep interfaces as the human-readable
// docstring surface, add Zod schemas alongside, pin equivalence. Deriving
// types from schemas would force-strip the docstrings that document the
// contract — a real cost on the surface mobile + web both bind to.
//
// `z.object` is loose by default (extra properties pass) — forward-compatible
// when a newer client posts a field the server hasn't shipped yet. Use
// `.strict()` only when the route MUST reject unknowns (none today).
// ---------------------------------------------------------------------------

import { z } from "zod";
import type { CarrierProvider } from "./carrier";
import { pinned } from "./pin";

export const CheckoutItemContextSchema = pinned<CheckoutItemContext>()(
  z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("listing") }),
    z.object({ kind: z.literal("offer"), offerId: z.string().min(1) }),
    z.object({ kind: z.literal("auction") }),
  ]),
);

// PR-E Move 2 (Sibling-F · F2): per-item `kind` discriminator dropped.
// Each per-branch item schema is flat — the root `CheckoutIntentSchema`
// discriminated union picks which item shape to apply.
export const CheckoutIntentFlatRateItemSchema =
  pinned<CheckoutIntentFlatRateItem>()(
    z.object({
      listingId: z.string().min(1),
      quantity: z.number().int().positive(),
      shippingMethodId: z.string().min(1),
      context: CheckoutItemContextSchema,
    }),
  );

export const CheckoutIntentLiveRateItemSchema =
  pinned<CheckoutIntentLiveRateItem>()(
    z.object({
      listingId: z.string().min(1),
      quantity: z.number().int().positive(),
      context: CheckoutItemContextSchema,
    }),
  );

/**
 * Back-compat schema alias. Pre-Move-2 this was the discriminated union; under
 * PR-E Move 2 the discriminator lives at root. Kept as a `z.union` (no
 * discriminator) so existing legacy consumers parsing a bare line still
 * compile — the root schema is the canonical entry point now.
 */
export const CheckoutIntentItemSchema = pinned<CheckoutIntentItem>()(
  z.union([CheckoutIntentFlatRateItemSchema, CheckoutIntentLiveRateItemSchema]),
);

export const CheckoutIntentAddressSchema = pinned<CheckoutIntentAddress>()(
  z.object({
    firstName: z.string(),
    lastName: z.string(),
    address: z.string(),
    apartment: z.string().optional(),
    city: z.string(),
    // PR-E Move 1 (Sibling-F · F2): state REQUIRED for all countries — see
    // interface docstring.
    state: z.string(),
    postalCode: z.string(),
    country: z.string(),
    email: z.string(),
    phoneCountryCode: z.string().optional(),
    phoneNumber: z.string().optional(),
  }),
);

export const CheckoutIntentExternalShippingSelectionSchema =
  pinned<CheckoutIntentExternalShippingSelection>()(
    z.object({
      rateId: z.string().min(1),
      shipmentId: z.string().min(1),
      amount: z.number().nonnegative(),
    }),
  );

// PR-E Move 2 (Sibling-F · F2): root-level discriminated union on `kind`.
// Mirrors `CheckoutPayloadSchema`'s structure. `externalShipping` is REQUIRED
// on the `live_rate` branch (was optional pre-Move-2) — illegal state "live
// rate without a rate-lock" is unrepresentable.
const checkoutIntentBaseShape = {
  currency: z.string().min(1),
  shippingAddress: CheckoutIntentAddressSchema,
  // Single-seller invariant root — see CheckoutIntent.sellerId docstring.
  sellerId: z.string().min(1),
} as const;

export const CheckoutIntentFlatRateSchema = pinned<CheckoutIntentFlatRate>()(
  z.object({
    ...checkoutIntentBaseShape,
    kind: z.literal("flat_rate"),
    items: z.array(CheckoutIntentFlatRateItemSchema).min(1),
  }),
);

export const CheckoutIntentLiveRateSchema = pinned<CheckoutIntentLiveRate>()(
  z.object({
    ...checkoutIntentBaseShape,
    kind: z.literal("live_rate"),
    items: z.array(CheckoutIntentLiveRateItemSchema).min(1),
    externalShipping: CheckoutIntentExternalShippingSelectionSchema,
  }),
);

export const CheckoutIntentSchema = pinned<CheckoutIntent>()(
  z.discriminatedUnion("kind", [
    CheckoutIntentFlatRateSchema,
    CheckoutIntentLiveRateSchema,
  ]),
);

export const CheckoutFundingSourceSchema = pinned<CheckoutFundingSource>()(
  z.enum(["paypal", "paylater", "card", "applepay", "googlepay", "venmo"]),
);

const WalletAttributesSchema = z.object({
  attributes: z
    .object({
      verification: z.object({ method: z.string().optional() }).optional(),
    })
    .optional(),
});

export const CheckoutPaymentSourceSchema = pinned<CheckoutPaymentSource>()(
  z.object({
    google_pay: WalletAttributesSchema.optional(),
    apple_pay: WalletAttributesSchema.optional(),
  }),
);

export const CreateOrderRequestSchema = pinned<CreateOrderRequest>()(
  z.object({
    intent: CheckoutIntentSchema,
    fundingSource: CheckoutFundingSourceSchema.optional(),
    paymentSource: CheckoutPaymentSourceSchema.optional(),
    mobileReturnUrl: z.string().optional(),
    mobileCancelUrl: z.string().optional(),
    mockCode: z.string().optional(),
  }),
);

// ---------------------------------------------------------------------------
// Response contract — `POST /api/paypal-orders/create` 2xx body
// ---------------------------------------------------------------------------
//
// Shape is trivial but EVERY field is load-bearing for mobile:
//   - `id`         — PayPal order id; missing it throws `Réponse PayPal sans
//                    order id.` 100% of the time (`apps/mobile/lib/api-paypal.ts`
//                    `createPayPalOrder()` lines 218-220).
//   - `approvalUrl`— optional buyer-redirect URL; mobile WebView flow falls
//                    back to PayPal-app deep-link when present.
//   - `debugId`    — PayPal-Debug-Id passthrough for support tracing.
//
// Renaming `id` → `orderId` (consistent with our internal naming convention)
// is the canonical breaking change this schema prevents. See API_RESPONSE_AUDIT
// §5 for the historical class of bug.

export interface CreateOrderResponse {
  /** PayPal Order ID. Required even on a successful create. */
  id: string;
  /** PayPal-Debug-Id passthrough for support tracing. */
  debugId: string;
  /** Buyer-approval URL. Absent when PayPal didn't return an order id (defensive). */
  approvalUrl?: string;
}

export const CreateOrderResponseSchema = pinned<CreateOrderResponse>()(
  z.object({
    id: z.string().min(1),
    debugId: z.string(),
    approvalUrl: z.string().optional(),
  }),
);
